import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { DollarSign, TrendingUp, CreditCard, Calendar, Download, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { pdfExportService } from '@/services/pdfExportService';
import { csvExportService } from '@/services/csvExportService';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface RevenueAnalyticsDashboardProps {
  entityId: string;
  entityType: 'team' | 'venue' | 'coach';
}

interface RevenueData {
  date: string;
  amount: number;
  type: string;
}

interface Transaction {
  id: string;
  amount: string;
  currency: string;
  transaction_type: string;
  payment_provider: string | null;
  status: string;
  created_at: string;
  metadata: any;
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

export const RevenueAnalyticsDashboard: React.FC<RevenueAnalyticsDashboardProps> = ({
  entityId,
  entityType
}) => {
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadRevenueData();
    setupRealtimeSubscription();

    return () => {
      supabase.channel('revenue-changes').unsubscribe();
    };
  }, [entityId, entityType]);

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('revenue-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `entity_id=eq.${entityId}`
        },
        (payload) => {
          console.log('Transaction change detected:', payload);
          loadRevenueData();
        }
      )
      .subscribe();

    return channel;
  };

  const loadRevenueData = async () => {
    try {
      setLoading(true);

      // Bypass TypeScript until Supabase types regenerate
      const { data, error } = await (supabase as any)
        .from('transactions')
        .select('*')
        .eq('entity_id', entityId)
        .eq('entity_type', entityType)
        .eq('status', 'completed')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transactions = (data as any) || [];

      if (!transactions || transactions.length === 0) {
        setHasData(false);
        setRevenueData([]);
        return;
      }

      setHasData(true);

      // Transform transactions into chart-friendly data
      const formattedData: RevenueData[] = transactions.map((txn) => {
        const date = new Date(txn.created_at);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        // Capitalize and clean up transaction type
        const type = txn.transaction_type.charAt(0).toUpperCase() + 
                     txn.transaction_type.slice(1).replace('-', ' ');
        
        return {
          date: monthYear,
          amount: parseFloat(txn.amount.toString()),
          type: type
        };
      });

      setRevenueData(formattedData);

    } catch (error) {
      console.error('Error loading revenue data:', error);
      setHasData(false);
      toast({
        title: 'Error',
        description: 'Failed to load revenue analytics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = revenueData.reduce((sum, item) => sum + item.amount, 0);
  const avgMonthlyRevenue = totalRevenue / 6;
  const projectedAnnualRevenue = avgMonthlyRevenue * 12;

  const monthlyData = revenueData.reduce((acc, curr) => {
    const existing = acc.find(item => item.month === curr.date);
    if (existing) {
      existing.total += curr.amount;
      existing[curr.type] = (existing[curr.type] || 0) + curr.amount;
    } else {
      acc.push({
        month: curr.date,
        total: curr.amount,
        [curr.type]: curr.amount
      });
    }
    return acc;
  }, [] as any[]);

  const revenueByType = revenueData.reduce((acc, curr) => {
    const existing = acc.find(item => item.name === curr.type);
    if (existing) {
      existing.value += curr.amount;
    } else {
      acc.push({ name: curr.type, value: curr.amount });
    }
    return acc;
  }, [] as any[]);

  const handleExportPDF = async () => {
    try {
      // Simple CSV-based PDF export for now
      const headers = ['month', 'total', 'Subscriptions', 'Bookings'];
      const csvContent = csvExportService.convertToCSV(monthlyData, headers);
      toast({
        title: 'CSV Export',
        description: 'Use CSV export for spreadsheet analysis',
        variant: 'default'
      });
      handleExportCSV();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export data',
        variant: 'destructive'
      });
    }
  };

  const handleExportCSV = () => {
    const headers = ['month', 'total', 'Subscriptions', 'Bookings'];
    const csv = csvExportService.convertToCSV(monthlyData, headers);
    csvExportService.downloadCSV(csv, `revenue-analytics-${new Date().toISOString().split('T')[0]}.csv`);
    toast({
      title: 'Success',
      description: 'Revenue data exported to CSV'
    });
  };

  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-sports-green">Revenue Analytics</CardTitle>
          <CardDescription>Loading revenue data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="text-gray-400">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-sports-green">Revenue Analytics</CardTitle>
          <CardDescription>Track your revenue streams and financial performance</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="border-blue-600/30 bg-blue-600/10">
            <AlertCircle className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-blue-300">
              <strong>Ready for Payment Data</strong>
              <p className="text-sm text-gray-300 mt-2">
                This dashboard is connected and ready to display revenue analytics. 
                Once you start processing payments through Stripe or PayPal, your transaction 
                data will automatically appear here with real-time updates.
              </p>
              <ul className="text-xs text-gray-400 mt-3 space-y-1 list-disc list-inside">
                <li>Revenue trends over time</li>
                <li>Payment type breakdown</li>
                <li>Monthly comparisons</li>
                <li>Projected annual revenue</li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Revenue Analytics
            </CardTitle>
            <CardDescription>Payment trends, subscription metrics, and earnings forecasts</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-muted/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue (6mo)</p>
                  <p className="text-2xl font-bold text-foreground">${totalRevenue.toLocaleString()}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-muted/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Monthly Revenue</p>
                  <p className="text-2xl font-bold text-foreground">${avgMonthlyRevenue.toFixed(0)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-muted/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Projected Annual</p>
                  <p className="text-2xl font-bold text-foreground">${projectedAnnualRevenue.toFixed(0)}</p>
                </div>
                <Calendar className="h-8 w-8 text-secondary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <Tabs defaultValue="trends" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
            <TabsTrigger value="forecast">Forecast</TabsTrigger>
          </TabsList>

          <TabsContent value="trends" className="mt-6">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke="hsl(var(--primary))" 
                  fill="hsl(var(--primary) / 0.3)"
                  name="Total Revenue ($)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </TabsContent>

          <TabsContent value="breakdown" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Subscriptions" fill="hsl(var(--primary))" />
                  <Bar dataKey="Bookings" fill="hsl(var(--secondary))" />
                </BarChart>
              </ResponsiveContainer>

              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={revenueByType}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="hsl(var(--primary))"
                    dataKey="value"
                  >
                    {revenueByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="forecast" className="mt-6">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={[...monthlyData, 
                { month: '2024-07', total: avgMonthlyRevenue * 1.05 },
                { month: '2024-08', total: avgMonthlyRevenue * 1.08 },
                { month: '2024-09', total: avgMonthlyRevenue * 1.12 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Projected Revenue ($)"
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-sm text-muted-foreground text-center mt-4">
              Forecast based on 6-month average with 5% monthly growth
            </p>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
